import { ClipboardList, Gauge, MessagesSquare, UserPlus } from "lucide-react";
import { usePageTitle } from "@/components/layout/AppLayout";
import { CtaLink, imgSrc, useContent, useText } from "@/lib/content";

const ICONS = [UserPlus, ClipboardList, Gauge, MessagesSquare];

const LEVELS = [
    ["Principiante", "0–25"],
    ["Esploratore", "26–50"],
    ["Praticante", "51–75"],
    ["Avanzato", "76–100"],
];

export default function Home() {
    usePageTitle("Assessment AI per le imprese");
    const t = useText();
    const { images } = useContent();
    const faq = (t("home.faq") || []).filter((it) => it.q && it.a);
    return (
        <>
            <section className="edi-dotgrid" aria-labelledby="home-title" data-testid="home-hero">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 lg:py-20 grid gap-10 lg:grid-cols-[1.1fr_1fr] items-center">
                    <div className="fade-up">
                        <p className="edi-eyebrow" data-testid="home-eyebrow">{t("home.eyebrow")}</p>
                        <h1 id="home-title" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-edi-text whitespace-pre-line" data-testid="home-title">
                            {t("home.title")}
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-edi-muted max-w-xl whitespace-pre-line" data-testid="home-intro">{t("home.intro")}</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <CtaLink cta={t("home.hero_cta")} className="btn-primary" testId="home-register-button" />
                        </div>
                    </div>
                    <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
                        <img
                            src={imgSrc(images.hero.url)}
                            alt={images.hero.alt}
                            className="edi-hero-img edi-hero-glow relative w-full aspect-[3/2] object-cover rounded-xl"
                            data-testid="home-hero-image"
                            width="1264"
                            height="848"
                        />
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16" aria-labelledby="how-title">
                <h2 id="how-title" className="text-2xl sm:text-3xl font-extrabold">{t("home.how_title")}</h2>
                <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {ICONS.map((Icon, i) => (
                        <li key={i} className="edi-card p-6 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                            <Icon aria-hidden="true" size={20} className="text-edi-muted" data-testid={`home-step-icon-${i + 1}`} />
                            <p className="edi-step-num mt-4" aria-hidden="true" data-testid={`home-step-num-${i + 1}`}>{String(i + 1).padStart(2, "0")}</p>
                            <h3 className="mt-1 text-lg font-bold">{t(`home.step${i + 1}_title`)}</h3>
                            <p className="mt-2 text-edi-muted whitespace-pre-line">{t(`home.step${i + 1}_text`)}</p>
                        </li>
                    ))}
                </ol>
            </section>

            {faq.length > 0 && (
                <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16" aria-labelledby="faq-title" data-testid="home-faq">
                    <h2 id="faq-title" className="text-2xl sm:text-3xl font-extrabold">Domande frequenti</h2>
                    <ul className="mt-8 grid gap-4 lg:grid-cols-2">
                        {faq.map((it, i) => (
                            <li key={i} className="edi-card p-6" data-testid={`home-faq-item-${i + 1}`}>
                                <h3 className="text-lg font-bold whitespace-pre-line break-words" data-testid={`home-faq-q-${i + 1}`}>{it.q}</h3>
                                <p className="mt-2 text-edi-muted whitespace-pre-line break-words" data-testid={`home-faq-a-${i + 1}`}>{it.a}</p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="edi-home-end bg-edi-surface border-t border-edi-border" aria-labelledby="levels-title">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 grid gap-8 lg:grid-cols-[1fr_1.4fr] items-start">
                    <div>
                        <h2 id="levels-title" className="text-2xl sm:text-3xl font-extrabold">{t("home.levels_title")}</h2>
                        <p className="mt-3 whitespace-pre-line">{t("home.levels_text")}</p>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-2">
                        {LEVELS.map(([name, range]) => (
                            <li key={name} className="rounded-xl border border-edi-border bg-edi-surface p-4 flex items-center justify-between gap-2">
                                <span className="font-heading font-bold text-lg">{name}</span>
                                <span className="edi-level-badge" data-testid={`home-level-badge-${name.toLowerCase()}`}>{range}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </>
    );
}
